import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Plus } from "lucide-react";
import { RatingBadge } from "@/components/RatingBadge";

const STAGES = ["Requested", "Matched", "Quoted", "Approved", "Scheduled", "Completed"];

function stageFor(job) {
  if (job.status === "cancelled") return "Cancelled";
  if (job.status === "completed") return "Completed";
  if (job.status === "in_progress" || job.status === "scheduled") return "Scheduled";
  if (job.status === "quoted") return "Quoted";
  if (job.status === "requested" && job.assignee_id) return "Matched";
  return "Requested";
}
const BADGE = { Requested: ["requested", "Requested"], Matched: ["matched", "Matched"], Quoted: ["quote", "Quote received"], Scheduled: ["matched", "Confirmed"], Completed: ["done", "Completed"], Cancelled: ["requested", "Cancelled"] };

function Stepper({ current }) {
  const idx = STAGES.indexOf(current);
  return (
    <div className="jobstepper">
      {STAGES.map((s, i) => (
        <div key={s} className={`step ${i < idx ? "done" : ""} ${i === idx ? "now" : ""}`}><div className="dot2" /><small>{s}</small></div>
      ))}
    </div>
  );
}

function Toggle({ label, desc, on, onToggle, testid }) {
  return (
    <div className="togglerow">
      <div><b>{label}</b><span>{desc}</span></div>
      <button className={`switch ${on ? "on" : ""}`} data-testid={testid} onClick={onToggle} aria-label={label}><i /></button>
    </div>
  );
}

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [quotes, setQuotes] = useState({});
  const [prefs, setPrefs] = useState({ sms: true, whatsapp: false, email: true });
  const [rDate, setRDate] = useState({});
  const [managing, setManaging] = useState(null);

  const load = useCallback(() => {
    api.get("/jobs").then(async (r) => {
      setJobs(r.data);
      const q = {};
      await Promise.all(r.data.filter((j) => j.status === "quoted").map(async (j) => {
        try { const res = await api.get(`/jobs/${j.id}/quotes`); if (res.data?.length) q[j.id] = res.data[0]; } catch (e) {}
      }));
      setQuotes(q);
    });
  }, []);

  useEffect(() => { load(); api.get("/me/notifications").then((r) => setPrefs(r.data)).catch(() => {}); }, [load]);

  const savePrefs = (next) => {
    setPrefs(next);
    api.patch("/me/notifications", next).catch(() => toast.error("Couldn't save preference"));
  };

  const approve = async (quoteId) => {
    try { await api.post(`/quotes/${quoteId}/accept`); toast.success("Quote approved — Olivia will confirm scheduling."); load(); }
    catch (e) { toast.error("Could not approve quote"); }
  };

  const reschedule = async (id) => {
    const d = rDate[id];
    if (!d) return;
    try {
      await api.patch(`/jobs/${id}/reschedule`, { preferred_date: d });
      toast.success("New date requested — Olivia will confirm with your partner.");
      setRDate({ ...rDate, [id]: "" }); setManaging(null); load();
    } catch (e) { toast.error("Could not reschedule"); }
  };

  const cancelBooking = async (id) => {
    if (!window.confirm("Cancel this booking? Your assigned partner will be notified.")) return;
    try {
      await api.patch(`/jobs/${id}/status`, { status: "cancelled" });
      toast.success("Booking cancelled. We've let your partner know.");
      setManaging(null); load();
    } catch (e) { toast.error("Could not cancel booking"); }
  };

  return (
    <section className="od-section od-wrap" data-testid="customer-dashboard">
      <div className="od-eyebrow">Your dashboard</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "6px 0 30px", flexWrap: "wrap", gap: 12 }}>
        <h1 className="serif" style={{ fontSize: "2.1rem", fontWeight: 500, margin: 0 }}>Welcome back, {user.name.split(" ")[0]}.</h1>
        <Link to="/book" data-testid="cust-book-cta" className="btn primary"><Plus style={{ width: 16, height: 16 }} /> Book a new job</Link>
      </div>

      <div className="card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 30, flexWrap: "wrap", gap: 16 }}>
        <div>
          <div className="od-eyebrow" style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}><Sparkles className="icon" style={{ width: 16, height: 16 }} /> Olivia concierge</div>
          <h3 style={{ margin: 0, fontWeight: 500 }}>Not sure what you need? Describe it in plain English.</h3>
          <p style={{ marginTop: 6, color: "var(--taupe)" }}>Our concierge categorises the job, sets urgency and suggests the right trade.</p>
        </div>
        <Link to="/book" data-testid="cust-ai-cta" className="btn secondary">Open AI booking</Link>
      </div>

      <div className="togglecard" style={{ marginBottom: 34 }}>
        <div className="od-eyebrow" style={{ marginBottom: 4 }}>Notifications</div>
        <Toggle label="SMS updates" desc="Text messages for booking & job status changes" on={prefs.sms} onToggle={() => savePrefs({ ...prefs, sms: !prefs.sms })} testid="pref-sms" />
        <Toggle label="WhatsApp updates" desc="Same updates, sent via WhatsApp instead of SMS" on={prefs.whatsapp} onToggle={() => savePrefs({ ...prefs, whatsapp: !prefs.whatsapp })} testid="pref-whatsapp" />
        <Toggle label="Email updates" desc="Confirmations, quotes and receipts by email" on={prefs.email} onToggle={() => savePrefs({ ...prefs, email: !prefs.email })} testid="pref-email" />
      </div>

      <h2 className="serif" style={{ fontSize: "1.4rem", fontWeight: 500, marginBottom: 16 }}>Your jobs</h2>
      {jobs.length === 0 ? (
        <div className="card" data-testid="cust-empty" style={{ textAlign: "center", color: "var(--taupe)", padding: 40 }}>No jobs yet — book your first.</div>
      ) : jobs.map((j) => {
        const stage = stageFor(j);
        const [bcls, blabel] = BADGE[stage] || ["requested", j.status];
        const quote = quotes[j.id];
        return (
          <div className="jobcard" key={j.id} data-testid={`job-card-${j.id}`}>
            <div className="head">
              <div><div className="od-eyebrow" style={{ marginBottom: 4 }}>{j.category}</div><h3 style={{ margin: 0, fontWeight: 500 }}>{j.title}</h3></div>
              <span className={`status-badge ${bcls}`}>{blabel}</span>
            </div>
            <p style={{ marginTop: 10, color: "var(--taupe)" }}>{j.description}</p>
            <Stepper current={stage} />
            {j.assignee_name && (
              <div className="personmini">
                <div className="avatar">{j.assignee_name.split(" ").map((n) => n[0]).slice(0, 2).join("")}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <b>{j.assignee_name}</b>
                    {j.assignee_type === "trade" && <RatingBadge rating={j.assignee_rating} count={j.assignee_review_count} testid={`assignee-rating-${j.id}`} />}
                  </div>
                  <span>{j.price ? `£${j.price}` : "Confirmed"}{j.scheduled_at ? ` · ${j.scheduled_at}` : ""}</span>
                </div>
              </div>
            )}
            {quote && (
              <div className="quotebox">
                <div><span style={{ fontSize: ".78rem", color: "var(--taupe)" }}>Quote from your matched partner</span><div className="amt">£{quote.amount}</div></div>
                <div style={{ display: "flex", gap: 10 }}>
                  <Link to={`/jobs/${j.id}`} className="btn secondary sm">Details</Link>
                  <button className="btn rose sm" data-testid={`approve-quote-${j.id}`} onClick={() => approve(quote.id)}>Approve quote</button>
                </div>
              </div>
            )}
            {!quote && !j.assignee_name && (
              <p style={{ fontSize: ".82rem", marginTop: 10, color: "var(--taupe)" }}>Awaiting assignment — we'll notify you the moment a partner accepts.</p>
            )}
            {!["completed", "cancelled"].includes(j.status) && (
              <div style={{ marginTop: 16, borderTop: "1px solid var(--hairline)", paddingTop: 14 }}>
                {managing !== j.id ? (
                  <button className="btn secondary sm" data-testid={`manage-${j.id}`} onClick={() => setManaging(j.id)}>Manage booking</button>
                ) : (
                  <div data-testid={`manage-panel-${j.id}`}>
                    <label className="flabel" style={{ display: "block", marginBottom: 6 }}>Move to a new date</label>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                      <input className="finput" style={{ maxWidth: 180 }} type="date" data-testid={`reschedule-date-${j.id}`} value={rDate[j.id] || ""} onChange={(e) => setRDate({ ...rDate, [j.id]: e.target.value })} />
                      <button className="btn rose sm" data-testid={`reschedule-submit-${j.id}`} disabled={!rDate[j.id]} onClick={() => reschedule(j.id)}>Reschedule</button>
                      <button className="btn secondary sm" data-testid={`cancel-booking-${j.id}`} style={{ color: "#B04A4A", borderColor: "#B04A4A" }} onClick={() => cancelBooking(j.id)}>Cancel booking</button>
                      <button className="btn secondary sm" onClick={() => setManaging(null)}>Close</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}
